import type { SipEventPort, SipStoragePort, SipTimerPort } from './contracts';

type BrowserSipPorts = {
  sharedStorage: SipStoragePort;
  tabStorage: SipStoragePort;
  events: SipEventPort;
  timers: SipTimerPort;
  now: () => number;
  createId: () => string;
  createPeerConnection: (config: RTCConfiguration) => RTCPeerConnection;
  createMediaStream: (tracks: MediaStreamTrack[]) => MediaStream;
};

function unavailableStorage(): SipStoragePort {
  const unavailable = () => {
    throw new Error('Browser storage is unavailable');
  };
  return {
    getItem: unavailable,
    setItem: unavailable,
    removeItem: unavailable,
  };
}

function resolveStorage(
  browserWindow: Window | null,
  kind: 'shared' | 'tab',
): SipStoragePort {
  try {
    if (!browserWindow) return unavailableStorage();
    return kind === 'shared'
      ? browserWindow.localStorage
      : browserWindow.sessionStorage;
  } catch {
    return unavailableStorage();
  }
}

export function createBrowserSipPorts(): BrowserSipPorts {
  const browserWindow = typeof window === 'undefined' ? null : window;
  const sharedStorage = resolveStorage(browserWindow, 'shared');
  const tabStorage = resolveStorage(browserWindow, 'tab');

  return {
    sharedStorage,
    tabStorage,
    events: {
      addEventListener: (type, listener) => {
        browserWindow?.addEventListener(type, listener as EventListener);
      },
      removeEventListener: (type, listener) => {
        browserWindow?.removeEventListener(type, listener as EventListener);
      },
    },
    timers: {
      setTimeout: (handler, timeoutMs) => (browserWindow
        ? browserWindow.setTimeout(handler, timeoutMs)
        : globalThis.setTimeout(handler, timeoutMs) as unknown as number),
      clearTimeout: id => {
        if (browserWindow) browserWindow.clearTimeout(id);
        else globalThis.clearTimeout(id);
      },
      setInterval: (handler, timeoutMs) => (browserWindow
        ? browserWindow.setInterval(handler, timeoutMs)
        : globalThis.setInterval(handler, timeoutMs) as unknown as number),
      clearInterval: id => {
        if (browserWindow) browserWindow.clearInterval(id);
        else globalThis.clearInterval(id);
      },
    },
    now: () => Date.now(),
    createId: () => (
      globalThis.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random()}`
    ),
    createPeerConnection: config => {
      const PeerConnection = browserWindow?.RTCPeerConnection;
      if (!PeerConnection) {
        throw new Error('Этот браузер не поддерживает WebRTC');
      }
      return new PeerConnection(config);
    },
    createMediaStream: tracks => new MediaStream(tracks),
  };
}
